import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  FormControl,
  FormLabel,
  Input,
  Spinner,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Link as ChLink,
  Tag,
  Text,
  HStack,
  Divider,
  Center,
  VStack,
} from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminApiClient } from "../../api/adminApiClient";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { chakraStyles } from "../../components/fields/SelectField/ChakraReactSelectField";
import { SelectPeriod } from "../../components/fields/SelectPeriod/SelectPeriod";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { ReportType } from "../../enums/ReportType";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { useAdminApiRequest } from "../../hooks/useApiRequest";

interface ActionButtonProps {
  apiClient: AdminApiClient;
  reportType: ReportType;
  startDate1?: string | null;
  endDate1?: string | null;
  startDate2?: string | null;
  endDate2?: string | null;
  businessIds: string[];
}

const ActionButton = ({
  startDate1,
  startDate2,
  endDate1,
  endDate2,
  apiClient,
  businessIds,
  reportType,
}: ActionButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [result, setResult] = useState<string>();
  const [isNoData, setIsNoData] = useState(false);
  useEffect(() => {
    setIsLoading(false);
    setErrorMessage(undefined);
    setResult(undefined);
    setIsNoData(false);
  }, [startDate1, endDate1, startDate2, endDate2, businessIds]);

  const generateReport = async () => {
    setIsLoading(true);
    const result = await apiClient.generateComparativeReport(
      reportType,
      startDate1,
      endDate1,
      startDate2,
      endDate2,
      businessIds
    );
    if (result.ok) {
      setResult(result.data);
    } else {
      if (result.statusCode === 404) {
        setIsNoData(true);
      } else {
        setErrorMessage(result.message);
      }
    }
    setIsLoading(false);
  };
  const businessId = businessIds ? `-${businessIds.join("-")}` : "";
  return (
    <>
      {isLoading && <Spinner size="xs" />}
      {!isLoading && !errorMessage && !result && !isNoData && (
        <Button size="xs" onClick={generateReport}>
          Generate
        </Button>
      )}
      {!isLoading && !errorMessage && result && !isNoData && (
        <ChLink
          href={result}
          download={`comparative-${reportType}-${
            startDate1 ?? "no-start-date1"
          }-${endDate1 ?? "no-end-date1"}-${startDate2 ?? "no-start-date2"}-${
            endDate2 ?? "no-end-date2"
          }${businessId}.csv`}
        >
          Download
        </ChLink>
      )}
      {!isLoading && isNoData && <Tag>No Data</Tag>}
      {!isLoading && errorMessage && (
        <ErrorMessage>{errorMessage}</ErrorMessage>
      )}
    </>
  );
};

export const ComparativeReport = () => {
  const [startDate1, setStartDate1] = useState<string | undefined>("");
  const [endDate1, setEndDate1] = useState<string | undefined>("");
  const [startDate2, setStartDate2] = useState<string | undefined>("");
  const [endDate2, setEndDate2] = useState<string | undefined>("");
  const [isValid, setIsValid] = useState(false);
  const apiClient = useAdminApiClient();
  const [selectedBusinesses, setSelectedBusinesses] = useState<string[]>([]);

  useEffect(() => {
    if (startDate1 && endDate2 && startDate2 && endDate2) {
      setIsValid(true);
    }
  }, [startDate1, startDate2, endDate1, endDate2]);

  const onPeriod1Change = (
    startDate: string | undefined,
    endDate: string | undefined
  ) => {
    setStartDate1(startDate);
    setEndDate1(endDate);
  };

  const onPeriod2Change = (
    startDate: string | undefined,
    endDate: string | undefined
  ) => {
    setStartDate2(startDate);
    setEndDate2(endDate);
  };

  const {
    data: businesses,
    error: businessError,
    isLoading: businessLoading,
  } = useAdminApiRequest((client) => client.getBusinesses(""), []);

  const onSelectBusiness = (e: readonly { label: string; value: string }[]) => {
    setSelectedBusinesses(e.map((item) => item.value));
  };

  return (
    <>
      <PageHeading>
        <PageHeading.Title>Comparative Reports</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack w="100%" alignItems="flex-start" pb={4}>
          <Breadcrumb variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to="/admin/reports">
                Reports
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to="/admin/reports/comparative">
                Comparative Reports
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>

          <HStack spacing={6}>
            <VStack alignItems="flex-start" spacing={4}>
              <Text
                as="h2"
                fontSize="lg"
                fontWeight="bold"
                color="cherryUi.600"
              >
                Reporting Period:
              </Text>
              <HStack spacing={6}>
                <Box>
                  <FormControl>
                    <FormLabel>By Period</FormLabel>
                    <SelectPeriod onChange={onPeriod1Change} />
                  </FormControl>
                </Box>
                <Box>
                  <FormControl>
                    <FormLabel>Start date</FormLabel>
                    <Input
                      type="date"
                      placeholder="Start date"
                      onChange={(e) => setStartDate1(e.target.value)}
                      value={startDate1}
                    />
                  </FormControl>
                </Box>
                <Box>
                  <FormControl>
                    <FormLabel>End date</FormLabel>
                    <Input
                      type="date"
                      placeholder="End date"
                      onChange={(e) => setEndDate1(e.target.value)}
                      value={endDate1}
                    />
                  </FormControl>
                </Box>
              </HStack>
            </VStack>
            <Center h="120px">
              <Divider orientation="vertical" variant="horizontal-divider" />
            </Center>
            <VStack alignItems="flex-start" spacing={4}>
              <Text
                as="h2"
                fontSize="lg"
                fontWeight="bold"
                color="cherryUi.600"
              >
                Compare To:
              </Text>
              <HStack spacing={6}>
                <Box>
                  <FormControl>
                    <FormLabel>By Period</FormLabel>
                    <SelectPeriod onChange={onPeriod2Change} />
                  </FormControl>
                </Box>
                <Box>
                  <FormControl>
                    <FormLabel>Start date</FormLabel>
                    <Input
                      type="date"
                      placeholder="Start date"
                      onChange={(e) => setStartDate2(e.target.value)}
                      value={startDate2}
                    />
                  </FormControl>
                </Box>
                <Box>
                  <FormControl>
                    <FormLabel>End date</FormLabel>
                    <Input
                      type="date"
                      placeholder="End date 2"
                      onChange={(e) => setEndDate2(e.target.value)}
                      value={endDate2}
                    />
                  </FormControl>
                </Box>
              </HStack>
            </VStack>
          </HStack>

          <FormControl maxW="lg">
            <FormLabel fontSize="lg" fontWeight="bold" color="cherryUi.600">
              Filter by Business
            </FormLabel>
            <Select
              isMulti
              options={
                businesses?.Results.map((item) => {
                  return {
                    label: item.DisplayName!,
                    value: item.Meta!.Id!,
                  };
                }) || []
              }
              chakraStyles={chakraStyles}
              isLoading={businessLoading}
              placeholder="Select businesses"
              onChange={onSelectBusiness}
            />
          </FormControl>
        </VStack>
        {!isValid && (
          <Box
            alignSelf="flex-start"
            p={4}
            border="2px"
            borderRadius="lg"
            borderColor="#c41836"
            w="100%"
          >
            <Text>Please set the dates for both periods.</Text>
          </Box>
        )}
        <Box w="100%" pt={5}>
          <Table w="100%" size="sm">
            <Thead>
              <Tr>
                <Th w="75%">Report Type</Th>
                <Th>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td w="75%">Cherry Pay Card Status</Td>
                <Td>
                  {isValid && (
                    <ActionButton
                      apiClient={apiClient}
                      reportType={ReportType.CherryPay_CardStatus}
                      startDate1={startDate1}
                      endDate1={endDate1}
                      startDate2={startDate2}
                      endDate2={endDate2}
                      businessIds={selectedBusinesses}
                    />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td w="75%">Cherry Pay Card Program Type</Td>
                <Td>
                  {isValid && (
                    <ActionButton
                      apiClient={apiClient}
                      reportType={ReportType.CherryPay_CardProgramType}
                      startDate1={startDate1}
                      endDate1={endDate1}
                      startDate2={startDate2}
                      endDate2={endDate2}
                      businessIds={selectedBusinesses}
                    />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td w="75%">Cherry Pay Card Funds Loaded</Td>
                <Td>
                  {isValid && (
                    <ActionButton
                      apiClient={apiClient}
                      reportType={ReportType.CherryPay_CardFundsLoaded}
                      startDate1={startDate1}
                      endDate1={endDate1}
                      startDate2={startDate2}
                      endDate2={endDate2}
                      businessIds={selectedBusinesses}
                    />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td w="75%">Cherry Pass Downloaded</Td>
                <Td>
                  {isValid && (
                    <ActionButton
                      apiClient={apiClient}
                      reportType={ReportType.CherryPass_Downloaded}
                      startDate1={startDate1}
                      endDate1={endDate1}
                      startDate2={startDate2}
                      endDate2={endDate2}
                      businessIds={selectedBusinesses}
                    />
                  )}
                </Td>
              </Tr>
              <Tr>
                <Td w="75%">Cherry Play Promotion Coupon Summary</Td>
                <Td>
                  {isValid && (
                    <ActionButton
                      apiClient={apiClient}
                      reportType={ReportType.CherryPlay_PromotionCouponSummary}
                      startDate1={startDate1}
                      endDate1={endDate1}
                      startDate2={startDate2}
                      endDate2={endDate2}
                      businessIds={selectedBusinesses}
                    />
                  )}
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>
      </PageContent>
    </>
  );
};
